import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from 'react';
import type { Cliente, WalletCard } from '../types';

interface UserSession {
  status: boolean;
  user?: {
    id: string;
    username: string;
    email: string;
    name: string;
    role: string;
  };
  cliente?: Cliente;
}

interface UserContextType {
  userData: UserSession | null;
  setUserData: React.Dispatch<React.SetStateAction<UserSession | null>>;
  updateAvatar: (newAvatar: string, newMimeType: string) => void;
  userWallet: WalletCard[] | null;
  setUserWallet: React.Dispatch<React.SetStateAction<WalletCard[] | null>>;
  updateCardColor: (id: string, color: string) => void;
  getWalletId: () => string | null;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userData, setUserData] = useState<UserSession | null>(() => {
    const stored = sessionStorage.getItem('auth');
    return stored ? JSON.parse(stored) : null;
  });

  const [userWallet, setUserWallet] = useState<WalletCard[] | null>(() => {
    const stored = sessionStorage.getItem('wallet');
    return stored ? JSON.parse(stored) : null;
  });

  useEffect(() => {
    if (userData) {
      sessionStorage.setItem('auth', JSON.stringify(userData));
    }

    if (userWallet) {
      sessionStorage.setItem('wallet', JSON.stringify(userWallet));
    }
  }, [userData, userWallet]);

  const updateAvatar = (newAvatar: string, newMimeType: string): void => {
    if (!userData?.cliente) return;

    const updatedCliente = {
      ...userData.cliente,
      AVATAR: newAvatar,
      MIME_TYPE: newMimeType,
    };

    setUserData({
      ...userData,
      cliente: updatedCliente,
    });
  };

  const updateCardColor = (id: string, color: string): void => {
    setUserWallet((prev) =>
      prev
        ? prev.map((item) =>
            item.ID === id ? { ...item, theme_color_hex: color } : item
          )
        : null
    );
  };

  const getWalletId = (): string | null => {
    if (Array.isArray(userWallet) && userWallet.length > 0) {
      return userWallet[0]?.ID || null;
    }
    return null;
  };

  const value: UserContextType = {
    userData,
    setUserData,
    updateAvatar,
    userWallet,
    setUserWallet,
    updateCardColor,
    getWalletId,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
